import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { User, ApiResponse, WorkRecord, ActiveTimer } from '../types';
import { Clock, Plus, Download, SearchX, Loader2, MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
import ConfirmModal from '../components/ConfirmModal';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import MenuButton from '../components/table/MenuButton';
import HelpTip from '../components/ui/HelpTip';
import Tabs from '../components/ui/Tabs';
import Dialog from '../components/ui/Dialog';
import Field, { FormErrorSummary, rules, useFormValidation, type FormSchema } from '../components/ui/Field';
import { editableSummary, summaryRequiredOnEdit } from '../components/worklogs/runningSummary';
import DataTable from '../components/DataTable';
import FilterBar, { type FilterChip, type FilterDef } from '../components/ui/FilterBar';
import { usePermissions } from '../hooks/usePermissions';
import { isPlainEmployee } from '../components/permissions/screenAccess';
import { useListParams } from '../hooks/useListParams';
import { formatDate, formatNumber } from '../utils/format';
import PeriodControl from '../components/worklogs/PeriodControl';
import WorkLogsKpiStrip, { type KpiCell } from '../components/worklogs/WorkLogsKpiStrip';
import WorkRecordSegments from '../components/worklogs/WorkRecordSegments';
import RunningNowBlock from '../components/worklogs/RunningNowBlock';
import ActiveNowPanel from '../components/worklogs/ActiveNowPanel';
import { workLogQueryParams, type WorkLogType } from '../components/worklogs/workLogsFiltersModel';
import {
  effectiveTab, employeeChoices, shownTab, stripTabs, hasCustomerOrgFilter, hasEmployeeFilter, hasMoreRows, kpiQueryParams, kpiSpecs, listLimit, showWorkerColumn, tabScope, viewFromParam,
  workLogsRole, workRecordQueryParams,
  type KpiSpec, type PageFilters, type ShownTab,
} from '../components/worklogs/workLogsPageModel';
import { engagementLabel } from '../utils/engagementLabel';
import {
  WORKLOG_FILTER_KEYS, WORKLOG_PARAMS, filterPatch, filtersFromParams, periodPatch,
} from '../components/worklogs/workLogsListParams';
import {
  formatHoursClock, itemTitle, workLogCard, workLogColumns, workLogHref,
  workLogRowActions, workRecordCard, workRecordColumns, workRecordRowActions,
} from '../components/worklogs/workLogsTable';
import { NewWorkLogModal } from '../components/NewWorkLogModal';
import WorkLogOverlapNotice from '../components/worklogs/WorkLogOverlapNotice';
import { useBillableBoxDisabled } from '../components/worklogs/useBillableAuthority';
import { parallelConfirmTimes, shortenedTimes, type ShortenedTimes } from '../components/worklogs/overlapModel';
import { undoableDeleteWorkLog, DELETE_KINDS } from '../utils/undoableDeletes';
import { useWithoutHidden } from '../utils/undoableActions';
import TimerSummaryModal, { timerTargetFromTimer } from '../components/TimerSummaryModal';
import DateTimeInput from '../components/ui/DateTimeInput';
import ChangeRequestBox from '../components/changeRequests/ChangeRequestBox';
import { isLockRefusal } from '../components/changeRequests/changeRequestModel';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { useClaudeMarks } from '../components/claude/useClaudeMarks';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstPickerOptions } from '../utils/internalWorkOption';
import { israelToday } from '../utils/israelTime';
import MoveLogsButton from '../components/worklogs/move/MoveLogsButton';

// The manual work-log form moved to components/NewWorkLogModal.tsx (T-16a);
// re-exported so existing imports from this page keep working.
export { NewWorkLogModal };

/**
 * Work logs (T-40, owner decision D8) on DataTable v2, by role (P-10d):
 *
 *  * Tabs (D-72) — manager: הכל / שלי / פתוחות / פעילות עכשיו; employee:
 *    הכל / פתוחות. "פעילות עכשיו" is who works right now (ActiveNowPanel).
 *  * My running timers are highlighted at the top of the list, in both views
 *    (RunningNowBlock — bug 98121: the active log did not show at all).
 *  * The two views "לפי רישום" / "לפי משימה" stay (PRESERVE #2); since C-13
 *    (clarity, A-10) the default is "לפי רישום" and ONE "?" explains both.
 *  * Filters (D-72): the period (היום / השבוע / החודש), customer, task,
 *    project, milestone (+ the existing engagement / type / time of day). The
 *    "ממתין לאישור" / "אושרו" filters and the "תצוגות" menu (D-83) are gone.
 *  * KPI strip (D-79 §5, PRESERVE #3; D-159; C-13): ONE thin text line of four
 *    numbers in hours — today · this week · this month · open entries.
 *  * C-26 (clarity, A-10): the toolbar is [תצוגה] [?] · [תקופה] · [סינון] · [⋯]
 *    and the one primary "רישום שעות" in the header (hidden on phones, where
 *    the floating "+" owns "חדש", D-16); the cycle status moved into "סינון".
 *  * D-159: 10 rows at a time in both views, "מציג 10 מתוך N" + "הצג עוד" (the
 *    server pages: limit grows by 10, `total` comes back); no "בדיקה" column;
 *    no "עובד" column for a worker who sees only their own logs.
 *
 * P-30c (D-174, QX-30): no row approval and no bulk "אישור (n)" here - only edit / delete.
 * The month's approval (D-69) and the service-hours approval (D-76) are elsewhere.
 */
export default function WorkLogsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const list = useListParams(WORKLOG_PARAMS);
  const p = list.values;
  const isAdmin = user?.role === 'admin';
  // `review=auto` ("רשומות אוטומטיות לבדיקה") is the OTE manager's only.
  const perms = usePermissions();
  const canApprove = perms.capabilities.isGeneralManager;
  // FIX-PERMS-SCOPE: a plain employee who sees every employee's logs through "כל העובדים" - read only on others' logs.
  const othersReadOnly = !perms.isAdmin && perms.seesAllEmployees('workLogs') && isPlainEmployee(perms.capabilities);
  const isInternal = !!user && ['admin', 'agent'].includes(user.role);
  const viewParam = viewFromParam(p.view);
  // SEC-2b (D-92, D-104) — the role of the page: the GM, a "lead" who sees
  // others' logs (AM / PM / TL — the server says so), or a plain employee.
  const { data: roleKpis } = useQuery({
    queryKey: ['work-logs', 'kpis', 'role'],
    queryFn: () => api.getWorkLogKpis({}),
    enabled: isInternal && !isAdmin,
    staleTime: 5 * 60_000,
  });
  const pageRole = workLogsRole(isAdmin, roleKpis?.data?.sees_others);
  const tab = effectiveTab(p.tab, pageRole);
  const scope = tabScope(tab);
  const [today] = useState(() => new Date());
  // FIX-B9 (D-168): "רשומות אוטומטיות לבדיקה" is the OTE manager's only; for anyone else `review=auto` narrows nothing.
  const filters = useMemo(() => {
    const f = filtersFromParams(p, today, user?.id);
    return f.review === 'auto' && !canApprove ? { ...f, review: '' as const } : f;
  }, [p, today, user?.id, canApprove]);
  // FIX-B9 (D-161) — the review queue ("דורש טיפול עכשיו") is a list of logs over
  // everyone the caller may see, so its total is the cube's number: logs view, no tab narrowing.
  const reviewMode = !!filters.review;
  const view = reviewMode ? 'logs' : viewParam;
  /** The list request: the URL filters + the tab ("שלי" = my logs, "פתוחות" = open cycles). */
  const listFilters = useMemo(
    () => (reviewMode ? { ...filters, who: undefined, openOnly: false } : { ...filters, who: scope.who, openOnly: scope.openOnly }),
    [filters, reviewMode, scope.who, scope.openOnly],
  );
  const pageFilters: PageFilters = {
    from: filters.from,
    to: filters.to,
    customerId: filters.customerId,
    organizationId: filters.organizationId,
    projectId: filters.projectId,
    engagementId: filters.engagementId,
    milestoneId: filters.milestoneId ?? '',
    ticketId: filters.ticketId ?? '',
    agentId: filters.agentId,
  };
  // D-159 — "הצג עוד": the list asks the server for 10 × pages rows; any change of
  // filters, tab or view starts again from 10.
  const [pages, setPages] = useState(1);
  const resetKey = JSON.stringify([listFilters, p.rstatus, view, tab]);
  useEffect(() => { setPages(1); }, [resetKey]);
  const limit = listLimit(pages);
  const stripTab = shownTab(tab, reviewMode);
  const showWorker = showWorkerColumn(pageRole, stripTab);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Consume `create=true` once (e.g. "log time" on the customer page opens the
  // new-entry dialog).
  useEffect(() => {
    if (list.searchParams.get('create') !== 'true') return;
    setShowCreate(true);
    list.update((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('create');
      return next;
    }, { replace: true });
  }, [list]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [stoppingTimer, setStoppingTimer] = useState<ActiveTimer | null>(null);

  // The requester list is `GET /users`, OTE staff only (a customer manager is
  // refused), so the filter is staff only — no empty filter, no 403.
  const canFilterByCustomer = isInternal;

  const { data: customersData } = useQuery({
    queryKey: ['work-log-customers'],
    queryFn: () => api.getUsers({ limit: 300 }),
    enabled: !!canFilterByCustomer,
  });

  // SEC-2b (D-104) — the customers (organizations) the server lets the caller list.
  const { data: orgsData } = useQuery({
    queryKey: ['work-log-organizations', 'with-internal'],
    // D-274 §1 — the work of "עבודה פנימית OTE" can be filtered too.
    queryFn: () => api.getOrganizations({ limit: 300, include_internal: 1 }),
    enabled: isInternal && hasCustomerOrgFilter(pageRole),
    retry: false,
  });
  // D-274 §1 — "עבודה פנימית OTE" first, apart from the customers.
  const internalOrgId = useInternalOrgId();
  const orgOptions = internalFirstPickerOptions(
    ((orgsData as ApiResponse<Array<{ id: string; name: string; is_internal?: boolean | null }>>)?.data) ?? [],
    internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY),
  );

  // P-26 (QX-97): "של אחרים" filters by employee - the OTE staff of the same users list.
  const employeeOptions = employeeChoices((customersData as ApiResponse<User[]>)?.data ?? []);
  const customerOptions = ((customersData as ApiResponse<User[]>)?.data ?? []).filter((u) =>
    ['customer_viewer', 'customer_manager'].includes(u.role)
  );

  const { data: projectsData } = useQuery({
    queryKey: ['work-log-projects'],
    queryFn: () => api.getProjects({ limit: 200 }),
  });

  const projectOptions = (projectsData?.data ?? []).filter(p => p.status === 'active');

  // "התקשרות" (P-02b §6.7): service / hour-bank hours have no project, so they
  // are found by the task's engagement. `/engagements` refuses a portal-only
  // viewer, so the filter is offered to the other roles only. History filters
  // keep archived customers (D-30). With a customer chosen, only the engagements
  // of that customer's organization are offered.
  const canFilterByEngagement = !!user && user.role !== 'customer_viewer';
  const { data: engagementsData } = useQuery({
    queryKey: ['work-log-engagements'],
    queryFn: () => api.getEngagements({ limit: 200 }),
    enabled: canFilterByEngagement,
    retry: false,
  });
  const selectedCustomerOrg = filters.customerId
    ? customerOptions.find((c) => c.id === filters.customerId)?.organization_id
    : undefined;
  const engagementOptions = (engagementsData?.data ?? []).filter(
    (e) => !selectedCustomerOrg || e.organization_id === selectedCustomerOrg,
  );

  // P-10d (D-72) — "אבן דרך" and "משימה": narrowed by the chosen project / milestone.
  const { data: milestonesData } = useQuery({
    queryKey: ['work-log-milestones', filters.projectId],
    queryFn: () => api.getAllMilestones({ limit: 200, project_id: filters.projectId || undefined }),
    enabled: isInternal,
    retry: false,
  });
  const milestoneOptions = ((milestonesData?.data ?? []) as Array<{ id: string; name: string; project_name?: string | null }>);
  const { data: tasksData } = useQuery({
    queryKey: ['work-log-tasks', filters.projectId, filters.milestoneId],
    queryFn: () => api.getTickets({
      task_type: 'task',
      limit: 300,
      project_id: filters.projectId || undefined,
      milestone_id: filters.milestoneId || undefined,
    }),
    enabled: isInternal,
    retry: false,
  });
  const taskOptions = (tasksData?.data ?? []) as Array<{ id: string; title: string }>;

  const showList = tab !== 'active';
  const { data, isLoading, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['work-logs', JSON.stringify(listFilters), limit],
    queryFn: () => api.getWorkLogs(workLogQueryParams(listFilters, limit)),
    enabled: view === 'logs' && showList,
    placeholderData: keepPreviousData,
  });

  // My running timers — highlighted at the top of the list (bug 98121).
  const { data: timerStatusData } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: isInternal,
    refetchInterval: 15000,
    staleTime: 0,
  });
  const myTimers = timerStatusData?.data?.timers ?? [];

  // --- Work cycles view (Stage G) ---
  const recordsStatus = p.rstatus as '' | 'open' | 'closed';
  const recordsParams = workRecordQueryParams(pageFilters, tab, recordsStatus, limit);
  const recordsQuery = useQuery({
    queryKey: ['work-records', JSON.stringify(recordsParams)],
    queryFn: () => api.getWorkRecords(recordsParams),
    enabled: view === 'records' && showList,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
  const recordsTotal = recordsQuery.data?.data?.total ?? 0;
  const workRecords = (recordsQuery.data?.data?.records ?? []) as WorkRecord[];

  // A cycle's segments (its expanded row): the records endpoint returns
  // aggregates, not the segments, so all logs are fetched once (when the first
  // row opens) and grouped by work_record_id.
  const [openRecords, setOpenRecords] = useState<Set<string>>(() => new Set());
  const segmentsQuery = useQuery({
    queryKey: ['work-logs', 'all-for-records'],
    queryFn: () => api.getWorkLogs({ limit: 500 }),
    enabled: view === 'records' && openRecords.size > 0,
  });
  const allSegments = segmentsQuery.data?.data;

  // KPI strip (D-79 §5) — server totals in the viewer's scope.
  const kpiParams = kpiQueryParams(pageFilters, stripTab);
  const kpisQuery = useQuery({
    queryKey: ['work-logs', 'kpis', JSON.stringify(kpiParams)],
    queryFn: () => api.getWorkLogKpis(kpiParams),
    enabled: isInternal,
    refetchInterval: 60_000,
  });

  // B.19/B.22 — deleting inside a locked month: admins may override with a reason.
  const [lockedDelete, setLockedDelete] = useState<{ id: string; reason: string; message: string } | null>(null);
  // FIX-B9 (P-27, D-166 s.11): hidden at once, DELETE after 6 s, "בטל" cancels it
  // (the toast replaces "הרישום נמחק"). A refusal brings the row back; a locked month
  // asks for the reason, as before.
  const requestDelete = (id: string, reason?: string) => {
    setLockedDelete(null);
    undoableDeleteWorkLog({
      qc: queryClient, id, reason,
      onError: (err: any) => {
        const code = err?.error?.error_code;
        const message = err?.error?.message || t('workLogs.deleteError');
        if (code === 'PERIOD_LOCKED_REASON_REQUIRED') setLockedDelete({ id, reason: '', message });
        else toast('error', message);
      },
    });
  };

  const canCreate = isInternal;
  const canEditDelete = isInternal;
  // DQ-043 (D-71, D-92) - times and delete only on one's own logs (the OTE manager: everyone's).
  // A lead who sees others' logs (AM / PM / TL) may only flip "לחיוב" there - the server's rule.
  const canChangeLog = (log: { agent_id?: string | null }) => isAdmin || (!!user?.id && log.agent_id === user.id);
  // FIX-PERMS-SCOPE (🟩 Q2): "כל העובדים" on this page opens the export too (the server exports in that scope, no money).
  const canExport = user && (['admin', 'customer_manager'].includes(user.role) || perms.seesAllEmployees('workLogs'));
  const showTimeColumns = isInternal;

  const loadedLogs = useMemo(() => data?.data ?? [], [data]);
  const workLogs = useWithoutHidden(DELETE_KINDS.workLog, loadedLogs);
  const logsTotal = data?.meta?.total ?? workLogs.length;
  const listFailed = !!error && !data;

  // the "שלי" tab exports my logs only (a lead's scope is wider than that tab).
  const exportAgentId = filters.agentId || (scope.who === 'mine' ? user?.id : undefined) || undefined;
  const handleExport = async () => {
    const token = localStorage.getItem('auth_token');
    const url = api.getExportWorkLogsUrl({
      start_date: filters.from || undefined,
      end_date: filters.to || undefined,
      customer_id: filters.customerId || undefined,
      // P-02b-4 D8 — the export follows the "התקשרות" filter too.
      engagement_id: filters.engagementId || undefined,
      // D-319 sec.9 - the CSV follows the employee / project / customer filters of the screen too (no visual change).
      organization_id: filters.organizationId || undefined,
      agent_id: exportAgentId,
      project_id: filters.projectId || undefined,
    });
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast('error', apiErrorMessage(body, t('workLogs.exportError')));
        return;
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `work-logs-${israelToday()}.csv`; // DQ-447: the Israeli day
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', t('workLogs.exportError'));
    }
  };

  /* ---------------- Filters and chips (all in the URL) ---------------- */
  const chips: FilterChip[] = [];
  if (filters.review) {
    chips.push({ key: 'review', label: t(`workLogs.list.chipReview.${filters.review}`), onRemove: () => list.set({ review: '' }, { drop: ['from'] }) });
  }
  if (p.agent === 'me') chips.push({ key: 'agent', label: t('workLogs.list.chipMine'), onRemove: () => list.set({ agent: '' }) });
  else if (filters.agentId) {
    // One employee (the "תיעוד הצוות" link): named from the users list when it has them.
    const agentName = ((customersData as ApiResponse<User[]>)?.data ?? []).find((u) => u.id === filters.agentId)?.name;
    chips.push({
      key: 'agent',
      label: agentName ? t('workLogs.list.chipAgent', { name: agentName }) : t('workLogs.list.chipAgentUnknown'),
      onRemove: () => list.set({ agent: '' }),
    });
  }
  // A task chosen from a link ("כל הלוגים של המשימה") that the picker does not list keeps a readable chip.
  const taskChoices = taskOptions.map((tk) => ({ value: tk.id, label: tk.title }));
  if (filters.ticketId && !taskChoices.some((o) => o.value === filters.ticketId)) {
    taskChoices.unshift({ value: filters.ticketId, label: t('workLogs.list.chipTask') });
  }
  const milestoneChoices = milestoneOptions.map((m) => ({ value: m.id, label: m.project_name ? `${m.name} · ${m.project_name}` : m.name }));
  if (filters.milestoneId && !milestoneChoices.some((o) => o.value === filters.milestoneId)) {
    milestoneChoices.unshift({ value: filters.milestoneId, label: t('workLogs.filter.oneMilestone') });
  }
  // Customer, task, project, milestone (D-72) + engagement; type and time of day apply to "רישומים" only.
  const filterDefs: FilterDef[] = [
    ...(view === 'logs' ? [{
      key: 'type',
      label: t('workLogs.filter.type'),
      value: filters.type === 'all' ? '' : filters.type,
      allLabel: t('workLogs.filter.types.all'),
      // P-37 (D-194 Q-177) — "מענה לקריאות": the reply logs alone.
      options: (['tickets', 'tasks', 'milestones', 'replies'] as const).map((tp) => ({ value: tp, label: t(`workLogs.filter.types.${tp}`) })),
      onChange: (v: string) => list.set(filterPatch({ type: (v || 'all') as WorkLogType })),
    }] : []),
    // QA-3 #7 — ONE "לקוח" field: where the customer (organization) filter
    // exists, the old requester filter (`customerId`) is not shown too.
    ...(canFilterByCustomer && !(isInternal && hasCustomerOrgFilter(pageRole)) ? [{
      key: 'customer',
      kind: 'search-select' as const,
      label: t('workLogs.filterCustomer'),
      value: filters.customerId,
      allLabel: t('workLogs.allCustomers'),
      options: customerOptions.map((c) => ({ value: c.id, label: c.name })),
      onChange: (v: string) => list.set(filterPatch({ customerId: v })),
    }] : []),
    // SEC-2b (D-92, D-104) — the customer (organization) filter for whoever
    // sees others' logs.
    ...(isInternal && hasCustomerOrgFilter(pageRole) ? [{
      key: 'org',
      kind: 'search-select' as const,
      label: t('workLogs.filter.organization'),
      value: filters.organizationId ?? '',
      allLabel: t('workLogs.filter.allOrganizations'),
      options: orgOptions,
      onChange: (v: string) => list.set(filterPatch({ organizationId: v })),
    }] : []),
    // P-26 (QX-97, D-167) - "של אחרים": filter by employee (URL `?agent=`).
    ...(isInternal && hasEmployeeFilter(pageRole, tab) ? [{
      key: 'agent',
      kind: 'search-select' as const,
      label: t('workLogs.filter.employee'),
      value: p.agent === 'me' ? '' : filters.agentId,
      allLabel: t('workLogs.filter.allEmployees'),
      options: employeeOptions,
      onChange: (v: string) => list.set({ agent: v }),
    }] : []),
    ...(isInternal ? [{
      key: 'task',
      kind: 'search-select' as const,
      label: t('workLogs.filter.task'),
      value: filters.ticketId ?? '',
      allLabel: t('workLogs.filter.allTasks'),
      options: taskChoices,
      onChange: (v: string) => list.set(filterPatch({ ticketId: v })),
    }] : []),
    {
      key: 'project',
      kind: 'search-select',
      label: t('workLogs.filterProject'),
      value: filters.projectId,
      allLabel: t('workLogs.allProjects'),
      options: projectOptions.map((pr) => ({ value: pr.id, label: pr.name })),
      // A new project clears the milestone and task chosen under the old one.
      onChange: (v) => list.set(filterPatch({ projectId: v, milestoneId: '', ticketId: '' })),
    },
    ...(isInternal ? [{
      key: 'milestone',
      kind: 'search-select' as const,
      label: t('workLogs.milestone'),
      value: filters.milestoneId ?? '',
      allLabel: t('workLogs.filter.allMilestones'),
      options: milestoneChoices,
      onChange: (v: string) => list.set(filterPatch({ milestoneId: v, ticketId: '' })),
    }] : []),
    ...(canFilterByEngagement ? [{
      key: 'engagement',
      kind: 'search-select' as const,
      label: t('workLogs.filterEngagement'),
      value: filters.engagementId,
      allLabel: t('workLogs.allEngagements'),
      options: engagementOptions.map((e) => ({ value: e.id, label: engagementLabel(e.child_name, e.type, t) || e.id })),
      onChange: (v: string) => list.set(filterPatch({ engagementId: v })),
    }] : []),
    // C-26 (clarity, A-10): the cycle status (הכול / פתוחות / סגורות) lives in "סינון", not on the bar.
    ...(view === 'records' && tab !== 'open' ? [{
      key: 'rstatus',
      label: t('workLogs.filter.recordStatus'),
      value: recordsStatus,
      allLabel: t('records.statusFilter.all'),
      options: (['open', 'closed'] as const).map((st) => ({ value: st, label: t(`records.statusFilter.${st}`) })),
      onChange: (v: string) => list.set({ rstatus: v }),
    }] : []),
    ...(view === 'logs' ? [{
      key: 'time',
      kind: 'dateRange' as const,
      input: 'time' as const,
      label: t('workLogs.filter.startTime'),
      fromLabel: t('workLogs.filterTimeFrom'),
      toLabel: t('workLogs.filterTimeTo'),
      from: filters.timeFrom,
      to: filters.timeTo,
      // Typing a time replaces the history entry.
      onRangeChange: (r: { from?: string; to?: string }) => list.set(filterPatch({
        ...(r.from !== undefined ? { timeFrom: r.from } : {}),
        ...(r.to !== undefined ? { timeTo: r.to } : {}),
      }), { replace: true }),
    }] : []),
  ];
  const clearPanel = () => list.set({ type: '', project: '', engagement: '', customer: '', ticket: '', milestone: '', tfrom: '', tto: '', rstatus: '' });
  const clearChips = () => list.set({ type: '', project: '', engagement: '', customer: '', ticket: '', milestone: '', tfrom: '', tto: '', agent: '', review: '' }, { drop: ['from'] });
  const clearEverything = () => list.set(
    Object.fromEntries(WORKLOG_FILTER_KEYS.map((k) => [k, ''])) as Partial<typeof p>,
    { drop: ['from'] },
  );
  const hasFilters = WORKLOG_FILTER_KEYS.some((k) => !!p[k]);

  /* ---------------- KPI strip (D-79 §5) ---------------- */
  const kpiState = kpisQuery.isLoading ? 'loading' : kpisQuery.isError && !kpisQuery.data ? 'error' : 'ok';
  const kpiLabel: Record<KpiSpec['key'], string> = {
    today: t('workLogs.kpi.today'),
    week: t('workLogs.kpi.week'),
    month: t('workLogs.kpi.month'),
    total: t('workLogs.kpi.total'),
    open: t('workLogs.kpi.open'),
    activeNow: t('workLogs.kpi.activeNow'),
  };
  const kpiCells: KpiCell[] = kpiSpecs(kpisQuery.data?.data, isAdmin).map((c) => ({
    key: c.key,
    label: kpiLabel[c.key],
    // C-13: hours as a clock (3:20 with the hours mark), never minutes.
    value: c.kind === 'minutes' ? formatHoursClock(c.value, t) : formatNumber(c.value),
    ...(c.key === 'open' && c.value > 0 ? { status: 'waiting' as const } : {}),
    // "עובדים פעילים עכשיו" opens the "פעילות עכשיו" tab.
    ...(c.key === 'activeNow' ? {
      sub: t('workLogs.kpi.activeNowLink'),
      pressed: tab === 'active',
      onToggle: () => list.set({ tab: tab === 'active' ? '' : 'active' }),
    } : {}),
  }));

  // FIX-CLAUDE-B (D-314 §11): the "Claude" tag on logs made through Claude.
  const claudeMarks = useClaudeMarks(workLogs.map((l) => l.id));
  const columns = workLogColumns({ t, type: filters.type, showTime: showTimeColumns, timer: null, showWorker, canEditTimes: !!canEditDelete, canChangeLog, claudeMarks });

  const logsEmpty = hasFilters ? (
    <EmptyState
      icon={SearchX}
      title={t('workLogs.noWorkLogs')}
      description={t('workLogs.states.noMatches')}
      action={<button type="button" onClick={clearEverything} className="btn btn-secondary">{t('workLogs.filter.clearAll')}</button>}
    />
  ) : (
    // C-05 (clarity, A-10): what happened and what to do, plus the one action "רישום שעות".
    <EmptyState
      icon={Clock}
      title={t('workLogs.noWorkLogs')}
      description={t('workLogs.emptyHint')}
      action={canCreate ? (
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4" aria-hidden="true" /> <span>{t('workLogs.newWorkLog')}</span>
        </button>
      ) : undefined}
    />
  );
  // C-29: the page "⋯" (export) sits at the end of the toolbar, like every other list.
  const pageMenu = canExport ? (
    <MenuButton
      label={t('workLogs.pageMenu')}
      showTooltip
      popoverClassName="w-56 p-1.5"
      triggerClassName="btn-icon text-text-2 hover:bg-surface-subtle hover:text-text aria-expanded:bg-surface-subtle touch-target"
      items={[{ key: 'export', label: t('common.exportCsv'), icon: <Download size={16} strokeWidth={1.75} aria-hidden="true" />, onSelect: () => void handleExport() }]}
    >
      <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden="true" />
    </MenuButton>
  ) : undefined;

  const tabLabel: Record<ShownTab, string> = {
    all: t('workLogs.tabs.all'),
    mine: t('workLogs.tabs.mine'),
    others: t('workLogs.tabs.others'),
    open: t('workLogs.tabs.open'),
    active: t('workLogs.tabs.active'),
  };

  return (
    <div>
      <PageHeader
        title={t('workLogs.title')}
        actions={canCreate && (
          // D-16 (clarity, C-27): on phones the floating "+" owns "רישום שעות"; the header primary steps aside there.
          <button type="button" className="btn btn-primary max-md:hidden" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" aria-hidden="true" /> <span>{t('workLogs.newWorkLog')}</span>
          </button>
        )}
      />

      {/* SEC-2b (D-104) — the role tabs: שלי (default) / של אחרים / פתוחות / פעילות עכשיו. */}
      {isInternal && (
        <Tabs
          className="mb-3"
          ariaLabel={t('workLogs.tabs.label')}
          value={stripTab}
          onChange={(k) => {
            // FU-65: "הכול" is the review queue's own mark; any real tab leaves the queue.
            if (k === 'all') return;
            list.set(reviewMode ? { tab: k === 'mine' ? '' : k, review: '' } : { tab: k === 'mine' ? '' : k });
          }}
          items={stripTabs(pageRole, reviewMode).map((k) => ({ key: k, label: tabLabel[k] }))}
        />
      )}

      {/* D-79 §5 — the KPI strip, for every tab. */}
      {isInternal && (
        <div className="mb-4">
          <WorkLogsKpiStrip heading={t('workLogs.kpi.heading')} state={kpiState} cells={kpiCells} />
        </div>
      )}

      {tab === 'active' ? (
        <ActiveNowPanel />
      ) : (
        <>
          {/* C-26 (clarity, A-10): one toolbar: [תצוגה] [?] · [תקופה] · [סינון] · [⋯]. */}
          <FilterBar
            className="mb-3"
            leading={(
              <>
                <Tabs
                  variant="segmented"
                  ariaLabel={t('workLogs.viewLabel')}
                  value={view}
                  onChange={(v) => list.set({ view: v === 'records' ? 'records' : '' })}
                  items={[
                    { key: 'logs', label: t('workLogs.viewLogs') },
                    { key: 'records', label: t('workLogs.tabRecords') },
                  ]}
                />
                <HelpTip title={t('workLogs.viewHelpLabel')} body={`${t('workLogs.viewHelp.logs')}\n\n${t('workLogs.viewHelp.records')}`} />
              </>
            )}
            filters={filterDefs}
            onClearFilters={clearPanel}
            chips={chips}
            onClearAll={clearChips}
            end={pageMenu}
          >
            <PeriodControl from={filters.from} to={filters.to} today={today} onChange={(r) => list.set(periodPatch(r, today))} />
          </FilterBar>

          {/* D-72 / bug 98121 — my active log, highlighted at the top of the list. */}
          {isInternal && <RunningNowBlock timers={myTimers} onStop={(tm) => setStoppingTimer(tm)} />}

          {view === 'logs' && (
            <DataTable
              columns={columns}
              data={workLogs}
              keyField="id"
              caption={t('workLogs.list.caption')}
              density="compact"
              stickyHeader
              mobile="cards"
              getRowHref={workLogHref}
              rowLabel={(log) => `${itemTitle(log, t)} · ${formatDate(log.start_time || log.created_at)}`}
              card={(log) => workLogCard(log, t, showTimeColumns, showWorker)}
              rowActions={canEditDelete ? (log) => (othersReadOnly && !canChangeLog(log) ? [] : workLogRowActions(log, {
                t,
                canEdit: !!canEditDelete,
                ownOrManager: canChangeLog(log),
                onEdit: () => setEditingId(log.id),
                onDelete: () => setConfirmDeleteId(log.id),
              })) : undefined}
              isLoading={isLoading}
              skeletonRows={8}
              error={listFailed ? error : undefined}
              errorMessage={t('workLogs.states.loadError')}
              onRetry={() => void refetch()}
              emptyState={logsEmpty}
            />
          )}
          {view === 'logs' && !isLoading && !listFailed && (
            <ShowMore shown={workLogs.length} total={logsTotal} fetching={isPlaceholderData} onMore={() => setPages((n) => n + 1)} />
          )}

          {/* Work cycles view (Stage G) — the default (D-79 §3) */}
          {view === 'records' && (
            <WorkRecordsView
              records={workRecords}
              showWorker={showWorker}
              isLoading={recordsQuery.isLoading}
              error={recordsQuery.isError && !recordsQuery.data ? recordsQuery.error : null}
              onRetry={() => void recordsQuery.refetch()}
              expanded={openRecords}
              onExpandedChange={setOpenRecords}
              renderSegments={(rec) => (
                <WorkRecordSegments
                  record={rec}
                  segments={(allSegments ?? []).filter((wl) => wl.work_record_id === rec.id)}
                  canEditTimes={!!canEditDelete && canChangeLog(rec)}
                  loading={segmentsQuery.isLoading}
                  error={segmentsQuery.isError && !segmentsQuery.data}
                  onRetry={() => void segmentsQuery.refetch()}
                />
              )}
              onOpenTicket={(rec) => navigate(`/tickets/${rec.ticket_id}?from=/work-logs`)}
              emptyState={logsEmpty}
            />
          )}
          {view === 'records' && !recordsQuery.isLoading && !(recordsQuery.isError && !recordsQuery.data) && (
            <ShowMore shown={workRecords.length} total={recordsTotal} fetching={recordsQuery.isPlaceholderData} onMore={() => setPages((n) => n + 1)} />
          )}
        </>
      )}

      {showCreate && (
        <NewWorkLogModal onClose={() => setShowCreate(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['work-logs'] }); setShowCreate(false); }} />
      )}

      {editingId && (
        <EditWorkLogModal workLogId={editingId} canChangeLog={canChangeLog} onClose={() => setEditingId(null)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['work-logs'] }); setEditingId(null); }} />
      )}

      {confirmDeleteId && (
        <ConfirmModal
          title={t('workLogs.deleteTitle')}
          description={t('workLogs.deleteConfirm')}
          confirmLabel={t('common.delete')}
          onConfirm={() => { requestDelete(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}

      <Dialog
        open={!!lockedDelete}
        onClose={() => setLockedDelete(null)}
        title={t('timeTasks.periodLock.title')}
        description={lockedDelete?.message}
        size="md"
        footer={(
          <>
            <button type="button" onClick={() => setLockedDelete(null)} className="btn btn-secondary">{t('common.cancel')}</button>
            <button
              type="button"
              onClick={() => lockedDelete && requestDelete(lockedDelete.id, lockedDelete.reason.trim())}
              disabled={(lockedDelete?.reason.trim().length ?? 0) < 3}
              className="btn btn-danger"
            >
              {t('timeTasks.periodLock.overrideDelete')}
            </button>
          </>
        )}
      >
        <label htmlFor="wl-locked-reason" className="label">{t('timeTasks.periodLock.reasonLabel')}</label>
        <textarea
          id="wl-locked-reason"
          className="input text-sm w-full"
          value={lockedDelete?.reason ?? ''}
          onChange={(e) => lockedDelete && setLockedDelete({ ...lockedDelete, reason: e.target.value })}
          placeholder={t('timeTasks.periodLock.reasonPlaceholder')}
          rows={3}
        />
      </Dialog>

      {stoppingTimer && (
        <TimerSummaryModal
          target={timerTargetFromTimer(stoppingTimer)}
          onClose={() => { setStoppingTimer(null); }}
          onDone={() => {
            setStoppingTimer(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
            queryClient.invalidateQueries({ queryKey: ['timer-status'] });
            queryClient.invalidateQueries({ queryKey: ['work-records'] });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Work cycles ("מחזורי עבודה", Stage G) on DataTable v2: one row per
// cycle (worker + ticket); the row opens the cycle's time segments.
// P-10d: the page-level KPI strip (D-79 §5) replaced this view's own strip.
// ============================================================
function WorkRecordsView({
  records,
  showWorker,
  isLoading,
  error,
  onRetry,
  expanded,
  onExpandedChange,
  renderSegments,
  onOpenTicket,
  emptyState,
}: {
  records: WorkRecord[];
  showWorker: boolean;
  isLoading: boolean;
  /** The rejection when the records failed and nothing is cached; null otherwise. */
  error: unknown;
  onRetry: () => void;
  expanded: ReadonlySet<string>;
  onExpandedChange: (next: Set<string>) => void;
  renderSegments: (rec: WorkRecord) => ReactNode;
  onOpenTicket: (rec: WorkRecord) => void;
  /** C-05 (clarity): the same empty state as the entries view (what to do + "רישום שעות"). */
  emptyState: ReactNode;
}) {
  const { t } = useTranslation();
  const claudeMarks = useClaudeMarks(records.map((r) => r.id));

  return (
    <DataTable
      columns={workRecordColumns({ t, showWorker, claudeMarks })}
      data={records}
      keyField="id"
      caption={t('workLogs.cycles.caption')}
      density="compact"
      stickyHeader
      mobile="cards"
      rowLabel={(rec) => `${rec.ticket_title || t('workLogs.noTitle')} · ${rec.agent_name ?? ''}`}
      card={(rec) => workRecordCard(rec, t, showWorker)}
      renderExpanded={renderSegments}
      expandedKeys={expanded}
      onExpandedChange={onExpandedChange}
      rowActions={(rec) => workRecordRowActions(rec, { t, onOpenTicket: () => onOpenTicket(rec) })}
      isLoading={isLoading}
      skeletonRows={5}
      error={error || undefined}
      errorMessage={t('records.loadError')}
      onRetry={onRetry}
      emptyState={emptyState}
    />
  );
}

/** D-159 — under the table: "מציג 10 מתוך N" and "הצג עוד" (adds another 10). */
function ShowMore({ shown, total, fetching, onMore }: { shown: number; total: number; fetching: boolean; onMore: () => void }) {
  const { t } = useTranslation();
  if (total <= 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-sm text-text-2">
      <span aria-live="polite">{t('workLogs.list.shownOf', { shown: Math.min(shown, total), total })}</span>
      {hasMoreRows(shown, total) && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onMore} disabled={fetching}>
          {fetching && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
          {t('workLogs.list.showMore')}
        </button>
      )}
    </div>
  );
}

function EditWorkLogModal({ workLogId, canChangeLog, onClose, onSuccess }: {
  workLogId: string; canChangeLog: (log: { agent_id?: string | null }) => boolean; onClose: () => void; onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [billable, setBillable] = useState(true);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  // B.19/B.22 — admin override reason for a locked month.
  const [overrideReason, setOverrideReason] = useState('');

  const { data } = useQuery({
    queryKey: ['work-log', workLogId],
    queryFn: () => api.getWorkLog(workLogId),
  });

  // Load the entry once. datetime-local wants LOCAL time; the earlier
  // `toISOString().slice(0, 16)` showed UTC and moved the entry on save.
  const loaded = data?.success ? data.data : null;
  // DQ-043 (D-71, D-92) - someone else's log: only "לחיוב" may change (GM / PM / account manager);
  // the times and the summary are shown, not edited. The box follows the server's own rule.
  const billableOnly = !!loaded && !canChangeLog(loaded);
  const billableDisabled = useBillableBoxDisabled(loaded?.ticket_id);
  // P-10d (D-71) — the times as loaded: only CHANGED times are sent, so a
  // summary edit never re-checks overlap / future (and keeps the seconds).
  const [loadedTimes, setLoadedTimes] = useState({ start: '', end: '' });
  useEffect(() => {
    if (!loaded) return;
    setSummary(editableSummary(loaded.summary));
    setBillable(loaded.billable);
    const start = loaded.start_time ? format(new Date(loaded.start_time), "yyyy-MM-dd'T'HH:mm") : '';
    const end = loaded.end_time ? format(new Date(loaded.end_time), "yyyy-MM-dd'T'HH:mm") : '';
    setEditStartTime(start);
    setEditEndTime(end);
    setLoadedTimes({ start, end });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per entry: later refetches must not overwrite edits
  }, [loaded?.id]);

  const mutation = useMutation({
    // D-291: `arg` = the shortened times ("לקצר אוטומטית עד").
    mutationFn: (arg: ShortenedTimes | void) => {
      const override = arg as ShortenedTimes | undefined;
      // HN-2: a blank summary (a timer segment without one) is not sent - the server refuses an empty one.
      const payload: any = { billable };
      if (billableOnly) return api.updateWorkLog(workLogId, payload); // DQ-043: the only field allowed here
      if (summary.trim()) payload.summary = summary;
      if (editStartTime && editStartTime !== loadedTimes.start) payload.start_time = new Date(editStartTime).toISOString();
      if (editEndTime && editEndTime !== loadedTimes.end) payload.end_time = new Date(editEndTime).toISOString();
      if (override) { payload.start_time = override.start_time; payload.end_time = override.end_time; }
      if (override?.confirm_parallel) payload.confirm_parallel = true; // D-299
      if (overrideReason.trim()) payload.override_reason = overrideReason.trim();
      return api.updateWorkLog(workLogId, payload);
    },
    onSuccess,
  });
  const lockCode = (mutation.error as any)?.error?.error_code as string | undefined;
  const needsOverrideReason = lockCode === 'PERIOD_LOCKED_REASON_REQUIRED' || overrideReason.length > 0;
  // D-347 §4: my own log that a report was already produced on (or another lock) - "בקשת שינוי" instead of only the error.
  const meId = useAuthStore((s) => s.user?.id);
  const offerRequest = !!loaded && !!meId && loaded.agent_id === meId && isLockRefusal(mutation.error);
  const [requestDelete, setRequestDelete] = useState(false);

  const formId = `edit-work-log-${workLogId}`;
  // HN-2: no summary on the entry (timer segment) -> changing its times does not force one; once typed, 5+ chars.
  const summaryRequired = !billableOnly && (!loaded || summaryRequiredOnEdit(loaded.summary) || summary.trim() !== '');
  const schema: FormSchema = useMemo(() => ({
    [`${formId}-end`]: [rules.after(`${formId}-start`, 'timer.endBeforeStart')],
    [`${formId}-summary`]: summaryRequired ? [rules.required(), rules.minLength(5)] : [],
  }), [formId, summaryRequired]);
  const v = useFormValidation(schema, {
    [`${formId}-start`]: editStartTime,
    [`${formId}-end`]: editEndTime,
    [`${formId}-summary`]: summary,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('workLogs.editTitle')}
      size="lg"
      footer={(
        <>
          {/* D-348: "העברת לוגים להתקשרות אחרת" - this log (the owner / the OTE manager). */}
          {loaded && !billableOnly && (
            <span className="me-auto">
              <MoveLogsButton
                testId="edit-log-move"
                className="btn btn-ghost btn-sm"
                logs={[{ id: loaded.id, organization_id: (loaded as { organization_id?: string | null }).organization_id ?? null,
                  engagement_id: (loaded as { engagement_id?: string | null }).engagement_id ?? null, ticket_id: loaded.ticket_id,
                  ticket_title: (loaded as { ticket_title?: string | null }).ticket_title ?? null, duration_minutes: loaded.duration_minutes }]}
                onMoved={onSuccess}
              />
            </span>
          )}
          <button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button type="submit" form={formId} disabled={mutation.isPending || (billableOnly && (billableDisabled || billable === loaded?.billable))} className="btn btn-primary">
            {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('form.save')}
          </button>
        </>
      )}
    >
      <form id={formId} onSubmit={v.submit(() => mutation.mutate())} noValidate className="space-y-4">
        {v.showSummary && (
          <FormErrorSummary
            errors={v.errors}
            labels={{ [`${formId}-start`]: t('timer.start'), [`${formId}-end`]: t('timer.end'), [`${formId}-summary`]: t('ticketDetail.summary') }}
            order={Object.keys(schema)}
          />
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id={`${formId}-start`} label={t('timer.start')} error={v.errors[`${formId}-start`]}>
            <DateTimeInput dir="ltr" className="input text-start" value={editStartTime} disabled={billableOnly}
              onChange={(e) => setEditStartTime(e.target.value)} onBlur={() => v.blur(`${formId}-start`)} />
          </Field>
          <Field id={`${formId}-end`} label={t('timer.end')} error={v.errors[`${formId}-end`]}>
            <DateTimeInput dir="ltr" className="input text-start" value={editEndTime} disabled={billableOnly} max={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) => setEditEndTime(e.target.value)} onBlur={() => v.blur(`${formId}-end`)} />
          </Field>
        </div>
        <Field id={`${formId}-summary`} label={t('ticketDetail.summary')} required={summaryRequired} hint={t('form.summaryHint')} error={v.errors[`${formId}-summary`]}>
          <textarea className="input" value={summary} disabled={billableOnly} onChange={(e) => setSummary(e.target.value)} onBlur={() => v.blur(`${formId}-summary`)} rows={4} />
        </Field>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={billable} disabled={billableDisabled} onChange={(e) => setBillable(e.target.checked)} className="size-4 accent-primary-600" />
          {t('common.billable')}
        </label>
        <WorkLogOverlapNotice
          error={mutation.error}
          fallback={t('workLogs.updateError')}
          busy={mutation.isPending}
          onShorten={(c) => { const times = shortenedTimes(c); if (times) mutation.mutate(times); }}
          onConfirmParallel={(c) => { const times = parallelConfirmTimes(c); if (times) mutation.mutate(times); }}
          onRetry={() => mutation.mutate(mutation.variables)}
        />
        {offerRequest && (
          <div className="space-y-2">
            <p className="text-sm text-text-2">{t('changeRequests.lockedLogHint')}</p>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={requestDelete} onChange={(e) => setRequestDelete(e.target.checked)} className="size-4 accent-primary-600" />
              {t('changeRequests.deleteInstead')}
            </label>
            <ChangeRequestBox
              idPrefix={formId}
              type="work_log"
              payload={() => {
                if (requestDelete) return { work_log_id: workLogId, action: 'delete' };
                const p: Record<string, unknown> = { work_log_id: workLogId, action: 'update' };
                if (editStartTime && editStartTime !== loadedTimes.start) p.start_time = new Date(editStartTime).toISOString();
                if (editEndTime && editEndTime !== loadedTimes.end) p.end_time = new Date(editEndTime).toISOString();
                if (summary.trim() && summary.trim() !== editableSummary(loaded?.summary).trim()) p.summary = summary.trim();
                return p.start_time || p.end_time || p.summary ? p : null;
              }}
              onSent={onClose}
            />
          </div>
        )}
        {needsOverrideReason && (
          <div>
            <label htmlFor={`${formId}-reason`} className="label">{t('timeTasks.periodLock.reasonLabel')}</label>
            <textarea id={`${formId}-reason`} className="input text-sm" value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={t('timeTasks.periodLock.reasonPlaceholder')} rows={2} />
          </div>
        )}
      </form>
    </Dialog>
  );
}
